'use client';

import { useEffect, useMemo, useState } from 'react';
import { WalletGate } from '@/components/wallet-gate';
import { describeApiError } from '@/lib/api-client';
import { track } from '@/lib/analytics';

import { SLOW_CADENCE_MS } from '../api/keys';
import { FeesAndCosts } from '../components/fees-and-capital';
import { CapitalAccount } from '../components/fees-and-capital-account';
import { OwnedPositions } from '../components/owned-positions';
import type { PeriodReturnRow } from '../components/period-returns';
import { PortfolioHeader } from '../components/portfolio-header';
import { PortfolioMetrics } from '../components/portfolio-metrics';
import { PortfolioPerformance } from '../components/portfolio-performance';
import { PortfolioRecoveryBanner } from '../components/portfolio-recovery-banner';
import { PositionsTable } from '../components/positions-table';
import { PortfolioReturnAttribution } from '../components/portfolio-return-attribution';
import { useInvestorAttribution } from '../hooks/use-investor-attribution';
import { useInvestorFees } from '../hooks/use-investor-fees';
import { useTransactions } from '../hooks/use-transactions';
import { useHoldingCurrencies } from '../hooks/use-holding-currencies';
import { useInvestorHoldings } from '../hooks/use-investor-holdings';
import { useInvestorPnl } from '../hooks/use-investor-pnl';
import { usePerformanceChart } from '../hooks/use-performance-chart';
import { usePeriodReturns } from '../hooks/use-period-returns';
import { useRiskMetrics } from '../hooks/use-risk-metrics';
import {
  BENCHMARK_OPTIONS,
  DEFAULT_BENCHMARK,
  isSameBenchmark,
  type BenchmarkSymbol
} from '@/lib/benchmarks';
import { getPeriodQuery, type PortfolioPeriod } from '../utils/period';

const HOLDINGS_PAGE_SIZE = 100;

/** What a failed currency name lookup changes on screen, in plain words. */
const CURRENCY_NAMES_NOTE =
  'Some currency names could not be loaded, so those currencies show a shortened address.';

export function PortfolioView() {
  const [period, setPeriod] = useState<PortfolioPeriod>('YTD');
  const [benchmarks, setBenchmarks] = useState<BenchmarkSymbol[]>([
    DEFAULT_BENCHMARK
  ]);
  const [positionsPage, setPositionsPage] = useState(1);

  const periodQuery = getPeriodQuery(period);
  // The first page, largest first: the headline totals and the composition
  // card. Totals and coverage are computed over the whole wallet whatever
  // the page, so this never moves with the table's pager.
  const holdings = useInvestorHoldings({
    page: 1,
    limit: HOLDINGS_PAGE_SIZE,
    sort: 'value'
  });
  // The All positions table pages on its own. On page 1 this is the query
  // above (same key, one request).
  const positions = useInvestorHoldings({
    page: positionsPage,
    limit: HOLDINGS_PAGE_SIZE,
    sort: 'value'
  });
  // Names for strategy currency addresses. `fx.rates` on any page lists the
  // currency of every vault on every page, so the first page is enough.
  const currencyNames = useHoldingCurrencies(holdings.data);
  const currencies = currencyNames.registry;
  const metrics = useRiskMetrics({ window: periodQuery.window });
  const performance = usePerformanceChart({
    from: periodQuery.from,
    to: periodQuery.to,
    interval: periodQuery.interval,
    benchmarks
  });
  // Recent flows only. The chart annotates at most a handful of markers and
  // shows the most recent clusters, so one page covers it — the return line
  // itself comes from the backend's index, not from these.
  const transactions = useTransactions(
    { page: 1, limit: 50 },
    // Chart annotations move with deposits, not by the minute.
    { cadenceMs: SLOW_CADENCE_MS }
  );
  // The period table compares against the first selected index only, so the
  // history request carries that one index and nothing is asked when none
  // is selected. Daily on purpose: each row measures the index between its
  // own window edges on the last close on or before each, and weekly marks
  // would land up to six days off at both ends.
  const comparedSymbol = benchmarks[0];
  const comparedBenchmarks = useMemo(
    () => (comparedSymbol ? [comparedSymbol] : []),
    [comparedSymbol]
  );
  const benchmarkHistory = usePerformanceChart(
    { interval: '1d', benchmarks: comparedBenchmarks },
    { enabled: comparedSymbol != null }
  );
  const periodReturns = usePeriodReturns();
  // One monthly-granularity fetch serves both the P&L card (window totals)
  // and the capital account roll forward (months).
  const pnl = useInvestorPnl({
    window: periodQuery.window,
    granularity: 'month'
  });
  const fees = useInvestorFees({ window: periodQuery.window });
  const attribution = useInvestorAttribution({ window: periodQuery.window });

  useEffect(() => {
    setPositionsPage(1);
  }, [holdings.scopeKey]);

  // A refresh can leave fewer positions than the page on screen (positions
  // sold): that page then comes back empty while the wallet still holds
  // something, and with one page left there is no pager to leave it by.
  // The page's own answer, never a placeholder from another page, says how
  // many pages exist now; the table moves to the last of them.
  const positionsLastPage =
    positions.data != null && !positions.isPlaceholderData
      ? Math.max(1, Math.ceil(positions.data.total / positions.data.limit))
      : undefined;
  useEffect(() => {
    if (positionsLastPage != null && positionsPage > positionsLastPage) {
      setPositionsPage(positionsLastPage);
    }
  }, [positionsLastPage, positionsPage]);

  // Every query on the page, so a failure anywhere is named in the banner
  // and refetched by its retry. The period table's rows and the benchmark
  // history used to sit outside this list: a failed row was a bare dash.
  const failedSources = [
    { name: 'holdings', query: holdings },
    ...(positionsPage === 1
      ? []
      : [{ name: `positions page ${positionsPage}`, query: positions }]),
    { name: 'metrics', query: metrics },
    { name: 'performance', query: performance },
    { name: 'transactions', query: transactions },
    { name: 'pnl', query: pnl },
    { name: 'fees', query: fees },
    { name: 'attribution', query: attribution },
    ...(comparedSymbol != null
      ? [{ name: 'benchmark history', query: benchmarkHistory }]
      : []),
    ...periodReturns.map((row) => ({
      name: `returns ${row.label}`,
      query: row.query
    })),
    // Only when a currency needed naming: see `useHoldingCurrencies`.
    ...(currencyNames.lookup
      ? [{ name: 'currency names', query: currencyNames.lookup }]
      : [])
  ].filter((source) => source.query.isError);
  const failedQueries = failedSources.map((source) => source.query);
  const failureDetail =
    failedSources.length > 0
      ? failedSources
          .map(
            (source) =>
              `${source.name}: ${describeApiError(source.query.error) ?? 'failed'}`
          )
          .join(' · ')
      : undefined;
  const hasDataError = failedQueries.length > 0;
  const isRetrying = failedQueries.some((query) => query.isFetching);
  const retryFailed = () => {
    // Two rows can share one query (the YTD row and the YTD headline).
    // `cancelRefetch: false` joins the fetch already in flight instead of
    // aborting it and asking again.
    void Promise.all(
      failedQueries.map((query) => query.refetch({ cancelRefetch: false }))
    );
  };

  const changePeriod = (nextPeriod: PortfolioPeriod) => {
    setPeriod(nextPeriod);
    track('portfolio.period_changed', { period: nextPeriod });
  };

  // The Net return metric card compares against the first selected overlay.
  const comparedBenchmark = BENCHMARK_OPTIONS.find(
    (option) => option.symbol === comparedSymbol
  );
  const periodRows: PeriodReturnRow[] = periodReturns.map((row) => ({
    label: row.label,
    window: row.window,
    metrics: row.query.data,
    isLoading: row.query.isLoading,
    isError: row.query.isError && !row.query.data
  }));
  // Matched by symbol: the response orders its benchmarks its own way.
  const periodBenchmark =
    comparedSymbol == null
      ? undefined
      : benchmarkHistory.data?.benchmarks.find((entry) =>
          isSameBenchmark(comparedSymbol, entry.symbol)
        );

  return (
    <WalletGate
      title="Connect to inspect your portfolio"
      description="Connect the approved wallet to load private holdings, exposure, and performance."
    >
      <div className="space-y-3 p-3 sm:p-4 lg:p-6">
        {hasDataError ? (
          <PortfolioRecoveryBanner
            onRetry={retryFailed}
            isRetrying={isRetrying}
            detail={failureDetail}
            notes={currencyNames.namesMissing ? [CURRENCY_NAMES_NOTE] : []}
          />
        ) : null}
        <PortfolioHeader period={period} onPeriodChange={changePeriod} />
        <PortfolioMetrics
          periodLabel={period}
          holdings={holdings.data}
          currencies={currencies}
          metrics={metrics.data}
          performance={performance.data}
          pnl={pnl.data}
          fees={fees.data}
          benchmark={comparedBenchmark}
          holdingsLoading={holdings.isLoading}
          holdingsError={holdings.isError && !holdings.data}
          metricsLoading={metrics.isLoading}
          metricsError={metrics.isError && !metrics.data}
          pnlLoading={pnl.isLoading}
          pnlError={pnl.isError && !pnl.data}
          feesLoading={fees.isLoading}
          feesError={fees.isError && !fees.data}
        />
        <PortfolioPerformance
          data={performance.data}
          transactions={transactions.data?.data}
          isLoading={performance.isLoading}
          isError={performance.isError && !performance.data}
          benchmarks={benchmarks}
          onBenchmarksChange={setBenchmarks}
          periodRows={periodRows}
          periodBenchmark={periodBenchmark}
          periodBenchmarkLoading={benchmarkHistory.isLoading}
          periodBenchmarkError={
            benchmarkHistory.isError && !benchmarkHistory.data
          }
        />
        <div className="grid gap-3 xl:grid-cols-2">
          <OwnedPositions
            holdings={holdings.data}
            currencies={currencies}
            isLoading={holdings.isLoading}
            isError={holdings.isError && !holdings.data}
            error={holdings.error}
          />
          <PortfolioReturnAttribution
            attribution={attribution.data?.attribution}
            isLoading={attribution.isLoading}
            isError={attribution.isError && !attribution.data}
          />
        </div>
        <div className="grid gap-3 xl:grid-cols-2">
          <FeesAndCosts
            fees={fees.data}
            isLoading={fees.isLoading}
            isError={fees.isError && !fees.data}
            periodLabel={period}
          />
          <CapitalAccount
            pnl={pnl.data}
            isLoading={pnl.isLoading}
            isError={pnl.isError && !pnl.data}
            periodLabel={period}
          />
        </div>
        <PositionsTable
          holdings={positions.data}
          currencies={currencies}
          isLoading={positions.isLoading}
          isError={positions.isError && !positions.data}
          isFetching={positions.isFetching && positions.isPlaceholderData}
          onPageChange={setPositionsPage}
        />
      </div>
    </WalletGate>
  );
}
